import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getAccessToken } from "../lib/supabaseClient";
import { toast } from "sonner";
import {
  Loader2, MapPin, DollarSign, ExternalLink, CheckCircle2, XCircle, Send,
  RefreshCw, ArrowLeft, Store, ShoppingBag, AlertCircle, Eye, Edit3,
} from "lucide-react";

import { formatEnum, formatBool, formatArray, formatDollars, formatPaymentMethod, PAYMENT_METHOD_LABELS } from "../lib/labels";

const API = process.env.REACT_APP_BACKEND_URL || "";
async function af(path, opts = {}) {
  const token = await getAccessToken();
  const headers = { ...(opts.headers || {}), Authorization: `Bearer ${token || ""}` };
  return fetch(`${API}${path}`, { ...opts, headers });
}
const fmt$ = (c) => (c == null ? "—" : `$${(c / 100).toLocaleString()}`);

const STATUS_TONE = {
  draft: "gray", published: "amber", reserved: "amber", sold: "green",
  withdrawn: "red", on_hold: "gray",
};

// -------------- Top-level: Opportunities list --------------
export default function AdminOperatorMarketplace() {
  const [tab, setTab] = useState("opportunities");
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div><div className="sb-eyebrow">Operator Marketplace</div>
          <h2 className="mt-1 text-[22px] font-black">Listings & Purchase Requests</h2></div>
        <div className="flex gap-1">
          {[["opportunities","Listings",Store],["requests","Purchase Requests",ShoppingBag]].map(([k,l,I]) => (
            <button key={k} onClick={() => setTab(k)}
                    className={`text-[11px] px-3 py-2 rounded ${tab===k?"bg-[#FFC107] text-[#080B0D] font-bold":"text-[#A1A1AA] hover:text-white"}`}
                    data-testid={`admin-mp-tab-${k}`}>
              <I size={11} className="inline mr-1" /> {l}
            </button>
          ))}
        </div>
      </div>
      {tab === "opportunities" ? <OpportunitiesList /> : <PurchaseRequestsList />}
    </div>
  );
}

function OppRow({ o, requestsCountKey }) {
  return (
    <tr className="border-t border-[#2A2F34] text-[12px]">
      <td className="py-3 pr-3"><div className="font-bold">{o.listing_title}</div>
        <div className="text-[11px] text-[#A1A1AA]"><MapPin size={10} className="inline" /> {o.public_city}, {o.public_state}</div></td>
      <td className="py-3 pr-3"><span className={`sb-pill sb-pill-${STATUS_TONE[o.status] || "gray"}`}>{o.status.replace("_"," ")}</span></td>
      <td className="py-3 pr-3 font-black text-[#FFC107]">{fmt$(o.asking_price_cents)}</td>
      <td className="py-3 pr-3">{o[requestsCountKey] ?? 0}</td>
      <td className="py-3 pr-3 text-[11px] text-[#A1A1AA]">{new Date(o.updated_at).toLocaleDateString()}</td>
    </tr>
  );
}

function OpportunitiesList() {
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    setRows(null);
    const q = status ? `?status=${status}` : "";
    const r = await af(`/api/admin/operator-opportunities${q}`);
    setRows(r.ok ? (await r.json()).rows : []);
  }, [status]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="sb-card p-4" data-testid="admin-mp-opps">
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-1 flex-wrap">
          {["", "draft", "published", "reserved", "sold", "withdrawn"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} className={`text-[11px] px-3 py-1.5 rounded ${status===s?"bg-[#FFC107] text-[#080B0D] font-bold":"text-[#A1A1AA] hover:text-white border border-[#2A2F34]"}`}
                    data-testid={`admin-mp-filter-${s || "all"}`}>
              {s || "All"}
            </button>
          ))}
        </div>
        <button onClick={load} className="text-[11px] text-[#A1A1AA] hover:text-white flex items-center gap-1"><RefreshCw size={11} /></button>
      </div>
      <div className="mt-3 overflow-x-auto">
        {rows == null ? <Loader /> : rows.length === 0 ? <Empty /> : (
          <table className="w-full text-left">
            <thead><tr className="text-[10px] uppercase text-[#A1A1AA] tracking-widest"><th className="pb-2 pr-3">Listing</th><th className="pb-2 pr-3">Status</th><th className="pb-2 pr-3">Price</th><th className="pb-2 pr-3">Requests</th><th className="pb-2 pr-3">Updated</th></tr></thead>
            <tbody>{rows.map(o => <OppRow key={o.id} o={o} requestsCountKey="active_requests" />)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function PurchaseRequestsList() {
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(null); // {action, req_id}
  const load = useCallback(async () => {
    setRows(null);
    // Aggregate: fetch all opps then flatten their requests
    const r = await af("/api/admin/operator-opportunities");
    if (!r.ok) { setRows([]); return; }
    const d = await r.json();
    const all = [];
    for (const o of d.rows) {
      const rr = await af(`/api/admin/operator-opportunities/${o.id}`);
      if (rr.ok) {
        const full = await rr.json();
        for (const req of (full.requests || [])) {
          all.push({ ...req, opp: { id: full.id, listing_title: full.listing_title, public_city: full.public_city, public_state: full.public_state, status: full.status, asking_price_cents: full.asking_price_cents } });
        }
      }
    }
    setRows(all.sort((a,b) => new Date(b.requested_at) - new Date(a.requested_at)));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function act(reqId, action, body = null) {
    setBusyId(reqId);
    const url = action === "mark-paid"
      ? `/api/admin/purchase-requests/${reqId}/mark-paid?confirm=true`
      : `/api/admin/purchase-requests/${reqId}/${action}`;
    const r = await af(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body || {}) });
    setBusyId(null);
    if (!r.ok) { const d = await r.json().catch(() => ({})); toast.error(d?.detail || "Failed"); return; }
    toast.success(action.replace("-", " ") + " ok");
    await load();
  }

  const REQ_TONE = { pending: "amber", approved: "amber", payment_pending: "amber", paid: "green", rejected: "red", cancelled: "gray", expired: "gray" };

  return (
    <div className="sb-card p-4" data-testid="admin-mp-requests">
      {rows == null ? <Loader /> : rows.length === 0 ? <Empty text="No purchase requests yet." /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead><tr className="text-[10px] uppercase text-[#A1A1AA] tracking-widest border-b border-[#2A2F34]"><th className="pb-2 pr-3">Operator</th><th className="pb-2 pr-3">Listing</th><th className="pb-2 pr-3">Status</th><th className="pb-2 pr-3">Price</th><th className="pb-2 pr-3">Actions</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[#2A2F34]" data-testid={`admin-req-row-${r.id}`}>
                  <td className="py-3 pr-3 text-[12px]">
                    <div className="font-bold">{r.operator.name}</div>
                    <div className="text-[11px] text-[#A1A1AA]">{r.operator.company || ""} · {r.operator.email}</div>
                    <div className="text-[10px] text-[#52525b]">Op: {r.operator.status}</div>
                  </td>
                  <td className="py-3 pr-3 text-[12px]"><div className="font-bold">{r.opp.listing_title}</div><div className="text-[11px] text-[#A1A1AA]">{r.opp.public_city}, {r.opp.public_state}</div></td>
                  <td className="py-3 pr-3"><span className={`sb-pill sb-pill-${REQ_TONE[r.status] || "gray"}`}>{r.status.replace("_"," ")}</span></td>
                  <td className="py-3 pr-3 font-black text-[#FFC107] text-[13px]">{fmt$(r.price_cents)}</td>
                  <td className="py-3 pr-3">
                    <div className="flex flex-wrap gap-1">
                      {r.status === "pending" && (
                        <>
                          <button onClick={() => act(r.id, "approve-buyer")} disabled={busyId===r.id} className="text-[10px] px-2 py-1 rounded bg-[#22C55E] text-[#080B0D] font-bold" data-testid={`admin-req-approve-${r.id}`}>
                            {busyId===r.id?<Loader2 size={10} className="animate-spin"/>:<CheckCircle2 size={10} />} Approve
                          </button>
                          <button onClick={() => act(r.id, "reject")} disabled={busyId===r.id} className="text-[10px] px-2 py-1 rounded border border-[#EF4444] text-[#EF4444]" data-testid={`admin-req-reject-${r.id}`}>
                            <XCircle size={10} /> Reject
                          </button>
                        </>
                      )}
                      {r.status === "approved" && (
                        <>
                          <button onClick={() => setConfirm({ action: "mark-paid", req_id: r.id, price: r.price_cents, operator_label: `${r.operator.company || r.operator.name}`, opp_label: r.opp.listing_title })} className="text-[10px] px-2 py-1 rounded bg-[#FFC107] text-[#080B0D] font-bold" data-testid={`admin-req-mark-paid-${r.id}`}>
                            <DollarSign size={10} /> Mark Paid
                          </button>
                          <button onClick={() => act(r.id, "cancel-reservation")} className="text-[10px] px-2 py-1 rounded border border-[#2A2F34] text-[#A1A1AA]" data-testid={`admin-req-cancel-${r.id}`}>
                            Cancel Reservation
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {confirm?.action === "mark-paid" && (
        <MarkPaidModal req={confirm} onClose={() => setConfirm(null)} onConfirm={(body) => { const id = confirm.req_id; setConfirm(null); act(id, "mark-paid", body); }} />
      )}
    </div>
  );
}

function MarkPaidModal({ req, onClose, onConfirm }) {
  const [form, setForm] = React.useState({
    amount_received_cents: req.price,
    payment_method: "ach_bank_transfer",
    payment_received_at: new Date().toISOString().slice(0,16), // datetime-local format
    external_payment_reference: "",
    payment_note: "",
  });
  function upd(k, v) { setForm({ ...form, [k]: v }); }
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-mark-paid-modal">
      <div className="sb-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-[18px] font-black">Confirm Payment &amp; Unlock Location Package</h3>
        <div className="mt-3 grid grid-cols-3 gap-2 text-[12px]">
          <div><div className="sb-eyebrow">Operator</div><div className="mt-1">{req.operator_label || "—"}</div></div>
          <div><div className="sb-eyebrow">Opportunity</div><div className="mt-1">{req.opp_label || "—"}</div></div>
          <div><div className="sb-eyebrow">Agreed Sale Price</div><div className="mt-1 text-[15px] font-black text-[#FFC107]">{formatDollars(req.price)}</div></div>
        </div>
        <div className="mt-4 p-3 border-2 border-[#F59E0B]/60 bg-[#F59E0B]/5 rounded text-[11.5px] text-[#F59E0B]">
          This action marks the opportunity <b>sold</b> and releases protected location details (address, manager contact, exact placement) to the approved operator. Only proceed after payment has actually been received.
        </div>
        <div className="mt-4 space-y-3">
          <label className="block"><div className="sb-label">Amount Received ($USD)</div>
            <input type="number" step="0.01" className="sb-input" value={form.amount_received_cents / 100}
                   onChange={(e) => upd("amount_received_cents", Math.round(parseFloat(e.target.value || 0) * 100))}
                   data-testid="admin-mark-paid-amount" /></label>
          <label className="block"><div className="sb-label">Payment Method</div>
            <select className="sb-input" value={form.payment_method} onChange={(e) => upd("payment_method", e.target.value)} data-testid="admin-mark-paid-method">
              {Object.entries(PAYMENT_METHOD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></label>
          <label className="block"><div className="sb-label">Payment Date</div>
            <input type="datetime-local" className="sb-input" value={form.payment_received_at}
                   onChange={(e) => upd("payment_received_at", e.target.value)} data-testid="admin-mark-paid-date" /></label>
          <label className="block"><div className="sb-label">External Reference (optional)</div>
            <input className="sb-input" value={form.external_payment_reference}
                   onChange={(e) => upd("external_payment_reference", e.target.value)} placeholder="Wire ref / invoice #" data-testid="admin-mark-paid-ref" /></label>
          <label className="block"><div className="sb-label">Internal Payment Note (admin only, never shown to operator)</div>
            <textarea className="sb-input" rows={2} value={form.payment_note}
                   onChange={(e) => upd("payment_note", e.target.value)} data-testid="admin-mark-paid-note" /></label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="sb-btn-secondary text-[11px]" style={{ padding: "9px 14px" }}>Cancel</button>
          <button onClick={() => {
            const payload = { ...form };
            if (payload.payment_received_at) payload.payment_received_at = new Date(payload.payment_received_at).toISOString();
            onConfirm(payload);
          }} className="sb-btn-primary text-[11px]" style={{ padding: "9px 14px" }} data-testid="admin-mark-paid-confirm">
            Confirm Payment &amp; Unlock Location Package
          </button>
        </div>
      </div>
    </div>
  );
}

function Loader() { return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#FFC107]" /></div>; }
function Empty({ text = "No listings yet." }) { return <div className="text-center py-12 text-[13px] text-[#A1A1AA]">{text}</div>; }

// -------------- Listing Preparation Form (used inside LocationDetail) --------------
export function ListingPrepTab({ loc, id, onReload }) {
  const [listing, setListing] = useState(undefined); // undefined=loading, null=none
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [sold, setSold] = useState(null);

  const load = useCallback(async () => {
    const r = await af(`/api/admin/locations/${id}/listing`);
    const d = r.ok ? await r.json() : { listing: null };
    setListing(d.listing);
    const r2 = await af(`/api/admin/locations/${id}/photos`);
    if (r2.ok) setPhotos((await r2.json()).rows || []);
    const r3 = await af(`/api/admin/locations/${id}/sold-summary`);
    if (r3.ok) { const j = await r3.json(); if (j.sold) setSold(j); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (listing) setF(listing);
    else if (listing === null) {
      // Seed defaults from location
      setF({
        listing_title: `${(loc.business_type || "Vending").replace(/_/g," ")} Opportunity`,
        public_city: loc.city || "", public_state: loc.state || "",
        approximate_area: "", business_type: loc.business_type || "",
        sanitized_description: "",
        traffic_summary: [
          loc.employees && `${loc.employees} employees`,
          loc.shifts && `${loc.shifts} shift${loc.shifts>1?"s":""}`,
          loc.open_24_7 && "24/7 facility",
        ].filter(Boolean).join(", "),
        employee_count: loc.employees || null,
        resident_count: loc.est_residents || null,
        visitors_per_day: loc.visitors || null,
        number_of_shifts: loc.shifts || null,
        operating_hours: loc.hours || "",
        open_24_7: !!loc.open_24_7,
        foot_traffic_tier: loc.foot_traffic_tier || "",
        current_vending_summary: loc.current_vending ? "Existing vending in place" : "No current vending",
        machine_types: loc.desired_machine_types || [],
        placement_summary: loc.placement_notes || "",
        public_placement_summary: `Employee break room / common area (${(loc.business_type || "vending").replace(/_/g, " ")}).`,
        financial_terms_summary: "",
        agreement_term_months: loc.preferred_term_months || null,
        exclusivity_summary: "",
        selected_public_photo_ids: [],
        asking_price_cents: 0,
        internal_pricing_notes: "",
      });
    }
  }, [listing, loc]);

  if (listing === undefined || f == null) return <Loader />;

  const status = listing?.status;
  const canEdit = !status || status === "draft";
  const isPublished = ["published","reserved","sold","on_hold"].includes(status);

  function upd(k, v) { setF({ ...f, [k]: v }); }
  function togglePhoto(pid) {
    const cur = new Set(f.selected_public_photo_ids || []);
    if (cur.has(pid)) cur.delete(pid); else cur.add(pid);
    setF({ ...f, selected_public_photo_ids: [...cur] });
  }
  function toggleMT(m) {
    const cur = new Set(f.machine_types || []);
    if (cur.has(m)) cur.delete(m); else cur.add(m);
    setF({ ...f, machine_types: [...cur] });
  }

  async function save(publish = false) {
    setSaving(true);
    const payload = { ...f };
    if (payload.asking_price_cents === "") payload.asking_price_cents = 0;
    if (typeof payload.asking_price_cents === "string") payload.asking_price_cents = Math.round(parseFloat(payload.asking_price_cents) * 100);
    // If field was in dollars, coerce cleanly
    for (const k of ["employee_count","resident_count","visitors_per_day","number_of_shifts","agreement_term_months"]) {
      if (payload[k] === "" || payload[k] == null) payload[k] = null;
      else payload[k] = parseInt(payload[k]);
    }
    const r = await af(`/api/admin/locations/${id}/listing`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    if (!r.ok) { const d = await r.json().catch(() => ({})); toast.error(d?.detail || "Save failed"); setSaving(false); return; }
    const newListing = (await r.json()).listing;
    if (publish) {
      const rr = await af(`/api/admin/listings/${newListing.id}/publish`, { method: "POST" });
      if (!rr.ok) { const d = await rr.json().catch(() => ({})); toast.error(d?.detail || "Publish failed"); setSaving(false); return; }
      toast.success("Published to operators");
    } else toast.success("Draft saved");
    setSaving(false); setConfirmPublish(false);
    await load();
    await onReload?.();
  }

  async function withdraw() {
    if (!listing) return;
    const r = await af(`/api/admin/listings/${listing.id}/withdraw`, { method: "POST" });
    if (!r.ok) { const d = await r.json().catch(() => ({})); toast.error(d?.detail || "Withdraw failed"); return; }
    toast.success("Withdrawn");
    await load();
  }

  const MT = ["snack","drink","combo","smart_cooler","coffee","fresh_food","micro_market"];

  return (
    <div className="space-y-4" data-testid="admin-listing-prep">
      {sold && (
        <div className="sb-card p-5 border-[#22C55E]/40" style={{ background: "rgba(34,197,94,.05)" }} data-testid="admin-sold-card">
          <div className="text-[#22C55E] text-[11px] uppercase tracking-widest font-bold flex items-center gap-1"><CheckCircle2 size={12} /> Sold Opportunity</div>
          <div className="mt-2 grid md:grid-cols-3 gap-3 text-[13px]">
            <div><div className="text-[10px] uppercase text-[#A1A1AA] tracking-widest">Buyer</div><div className="mt-1 font-bold">{sold.buyer.company || sold.buyer.name}</div><div className="text-[11px] text-[#A1A1AA]">{sold.buyer.email}</div></div>
            <div><div className="text-[10px] uppercase text-[#A1A1AA] tracking-widest">Sale Price</div><div className="mt-1 text-[20px] font-black text-[#FFC107]">{fmt$(sold.sale_price_cents)}</div></div>
            <div><div className="text-[10px] uppercase text-[#A1A1AA] tracking-widest">Assigned</div><div className="mt-1">{new Date(sold.assigned_at).toLocaleString()}</div></div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="sb-eyebrow">Operator Listing</div>
          {status && <span className={`sb-pill sb-pill-${STATUS_TONE[status] || "gray"} text-[10px]`}>{status}</span>}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPreview(true)} className="sb-btn-secondary text-[11px]" style={{ padding: "8px 12px" }} data-testid="admin-listing-preview">
            <Eye size={11} /> Preview
          </button>
          {isPublished && ["published","reserved","on_hold"].includes(status) && (
            <button onClick={withdraw} className="sb-btn-secondary text-[11px]" style={{ padding: "8px 12px", color: "#EF4444", borderColor: "rgba(239,68,68,.3)" }} data-testid="admin-listing-withdraw">
              Withdraw
            </button>
          )}
        </div>
      </div>

      {canEdit ? (
        <div className="sb-card p-5 space-y-4">
          <div className="text-[11px] text-[#A1A1AA]">All fields visible below become the sanitized listing operators see. Address/manager/scout fields are NEVER included.</div>
          <div className="grid md:grid-cols-2 gap-3">
            <TF k="Listing title *" v={f.listing_title} onChange={(x) => upd("listing_title", x)} testid="admin-listing-title" />
            <TF k="Business Type" v={f.business_type} onChange={(x) => upd("business_type", x)} />
            <TF k="Public city *" v={f.public_city} onChange={(x) => upd("public_city", x)} />
            <TF k="Public state *" v={f.public_state} onChange={(x) => upd("public_state", x)} />
            <TF k="Approximate area (optional)" v={f.approximate_area} onChange={(x) => upd("approximate_area", x)} />
            <TF k="Foot traffic tier" v={f.foot_traffic_tier} onChange={(x) => upd("foot_traffic_tier", x)} />
          </div>
          <label className="block"><div className="sb-label">Sanitized description (operator-safe copy) *</div>
            <textarea className="sb-input" rows={3} value={f.sanitized_description || ""} onChange={(e) => upd("sanitized_description", e.target.value)} data-testid="admin-listing-desc" /></label>
          <label className="block"><div className="sb-label">Traffic summary</div>
            <textarea className="sb-input" rows={2} value={f.traffic_summary || ""} onChange={(e) => upd("traffic_summary", e.target.value)} /></label>
          <div className="grid md:grid-cols-4 gap-3">
            <TF k="Employees" type="number" v={f.employee_count} onChange={(x) => upd("employee_count", x)} />
            <TF k="Residents" type="number" v={f.resident_count} onChange={(x) => upd("resident_count", x)} />
            <TF k="Visitors / day" type="number" v={f.visitors_per_day} onChange={(x) => upd("visitors_per_day", x)} />
            <TF k="Shifts" type="number" v={f.number_of_shifts} onChange={(x) => upd("number_of_shifts", x)} />
          </div>
          <div className="grid md:grid-cols-2 gap-3 items-end">
            <TF k="Operating hours" v={f.operating_hours} onChange={(x) => upd("operating_hours", x)} />
            <label className="flex items-center gap-2 text-[13px] pt-6"><input type="checkbox" checked={!!f.open_24_7} onChange={(e) => upd("open_24_7", e.target.checked)} /> Open 24/7</label>
          </div>
          <div>
            <div className="sb-label">Machine types (for listing)</div>
            <div className="flex flex-wrap gap-2 mt-1">
              {MT.map((m) => (
                <button type="button" key={m} onClick={() => toggleMT(m)}
                        className={`sb-pill ${(f.machine_types || []).includes(m) ? "sb-pill-gold" : "sb-pill-gray"}`}>
                  {m.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>
          <label className="block"><div className="sb-label">Public placement summary <span className="text-[#F59E0B]">(general — operators see BEFORE purchase)</span></div>
            <textarea className="sb-input" rows={2}
                      placeholder="e.g., Employee break room / common area. Do not include identifying details."
                      value={f.public_placement_summary || ""}
                      onChange={(e) => upd("public_placement_summary", e.target.value)}
                      data-testid="admin-listing-public-placement" /></label>
          <div className="text-[10.5px] text-[#A1A1AA] -mt-2">Full <code>placement_summary</code> below is unlocked to the buyer only after payment.</div>
          <label className="block"><div className="sb-label">Buyer placement summary (unlocked after payment)</div>
            <textarea className="sb-input" rows={2} value={f.placement_summary || ""} onChange={(e) => upd("placement_summary", e.target.value)} /></label>
          <div className="grid md:grid-cols-3 gap-3">
            <TF k="Financial terms summary" v={f.financial_terms_summary} onChange={(x) => upd("financial_terms_summary", x)} />
            <TF k="Term (months)" type="number" v={f.agreement_term_months} onChange={(x) => upd("agreement_term_months", x)} />
            <TF k="Exclusivity summary" v={f.exclusivity_summary} onChange={(x) => upd("exclusivity_summary", x)} />
          </div>
          <TF k="Current vending summary" v={f.current_vending_summary} onChange={(x) => upd("current_vending_summary", x)} />

          <div>
            <div className="sb-label">Public photos (tick to include as listing preview)</div>
            {photos.length === 0 ? <div className="text-[11.5px] text-[#A1A1AA]">No photos uploaded yet.</div> : (
              <div className="mt-2 grid grid-cols-3 md:grid-cols-4 gap-2">
                {photos.map((p) => {
                  const selected = (f.selected_public_photo_ids || []).includes(p.id);
                  return (
                    <button key={p.id} type="button" onClick={() => togglePhoto(p.id)}
                            className={`relative aspect-video rounded overflow-hidden border-2 ${selected ? "border-[#FFC107]" : "border-[#2A2F34]"}`}
                            data-testid={`admin-listing-photo-${p.id}`}>
                      {p.signed_url ? <img src={p.signed_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#111]" />}
                      {selected && <div className="absolute top-1 right-1 bg-[#FFC107] rounded-full p-0.5"><CheckCircle2 size={11} className="text-[#080B0D]" /></div>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-3 border-t border-[#2A2F34] pt-4">
            <label className="block">
              <div className="sb-label">Asking price ($USD) *</div>
              <input type="number" step="0.01" className="sb-input"
                     value={(f.asking_price_cents || 0) / 100}
                     onChange={(e) => upd("asking_price_cents", Math.round(parseFloat(e.target.value || 0) * 100))}
                     data-testid="admin-listing-price" />
            </label>
            <label className="block">
              <div className="sb-label">Internal pricing notes (never shown to operators)</div>
              <input className="sb-input" value={f.internal_pricing_notes || ""} onChange={(e) => upd("internal_pricing_notes", e.target.value)} />
            </label>
          </div>

          <div className="flex justify-end gap-2 border-t border-[#2A2F34] pt-4">
            <button onClick={() => save(false)} disabled={saving} className="sb-btn-secondary text-[11px]" style={{ padding: "10px 14px" }} data-testid="admin-listing-save">
              {saving?<Loader2 size={11} className="animate-spin"/>:null} Save Draft
            </button>
            <button onClick={() => setConfirmPublish(true)} disabled={saving || !f.asking_price_cents} className="sb-btn-primary text-[11px]" style={{ padding: "10px 14px" }} data-testid="admin-listing-publish">
              <Send size={11} /> Publish to Operators
            </button>
          </div>
        </div>
      ) : (
        <div className="sb-card p-5">
          <div className="text-[11px] text-[#A1A1AA] mb-2">Listing is <b>{status}</b> — read-only. Price is locked.</div>
          <ListingReadonly f={f} />
        </div>
      )}

      {preview && <PreviewModal f={f} onClose={() => setPreview(false)} />}
      {confirmPublish && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setConfirmPublish(false)}>
          <div className="sb-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[18px] font-black">Publish to operators?</h3>
            <p className="mt-2 text-[13px] text-[#A1A1AA]">Approved operators can immediately submit purchase requests at the price shown. Location status → <b>marketplace_ready</b>. Price will lock.</p>
            <div className="mt-3 text-[15px]">Asking price: <b className="text-[#FFC107]">{fmt$(f.asking_price_cents)}</b></div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirmPublish(false)} className="sb-btn-secondary text-[11px]" style={{ padding: "9px 14px" }}>Cancel</button>
              <button onClick={() => save(true)} className="sb-btn-primary text-[11px]" style={{ padding: "9px 14px" }} data-testid="admin-listing-confirm-publish">Confirm Publish</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TF({ k, v, onChange, testid, type = "text" }) {
  return (
    <label className="block">
      <div className="sb-label">{k}</div>
      <input type={type} className="sb-input" value={v == null ? "" : v} onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : e.target.value) : e.target.value)} data-testid={testid} />
    </label>
  );
}

function ListingReadonly({ f }) {
  return (
    <div className="grid md:grid-cols-2 gap-3 text-[13px]">
      <KV k="Title" v={f.listing_title} /><KV k="Price" v={fmt$(f.asking_price_cents)} />
      <KV k="City / State" v={`${f.public_city}, ${f.public_state}`} />
      <KV k="Business Type" v={f.business_type} />
      <KV k="Description" v={f.sanitized_description} wide />
      <KV k="Traffic" v={f.traffic_summary} wide />
      <KV k="Machine types" v={(f.machine_types || []).join(", ")} />
      <KV k="Placement" v={f.placement_summary} wide />
      <KV k="Financial" v={f.financial_terms_summary} />
      <KV k="Term" v={f.agreement_term_months ? `${f.agreement_term_months} months` : "—"} />
      <KV k="Exclusivity" v={f.exclusivity_summary} />
    </div>
  );
}

function PreviewModal({ f, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-listing-preview-modal">
      <div className="sb-card p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-[#FFC107] font-bold">Operator Preview</div>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white"><XCircle size={16} /></button>
        </div>
        <div className="mt-3 text-[11px] text-[#A1A1AA]">This is exactly what an approved operator sees. Address / manager / scout data is NEVER exposed.</div>
        <div className="mt-4">
          <div className="sb-pill sb-pill-gold text-[10px]">{f.business_type || "Vending"}</div>
          <h2 className="mt-2 text-[24px] font-black">{f.listing_title}</h2>
          <div className="text-[12px] text-[#A1A1AA]"><MapPin size={11} className="inline" /> {f.public_city}, {f.public_state}{f.approximate_area && ` · ${f.approximate_area}`}</div>
          <div className="mt-3 text-[28px] font-black text-[#FFC107]">{fmt$(f.asking_price_cents)}</div>
          {f.sanitized_description && <p className="mt-3 text-[13px] leading-relaxed">{f.sanitized_description}</p>}
          <ListingReadonly f={f} />
        </div>
      </div>
    </div>
  );
}

function KV({ k, v, wide }) {
  if (v == null || v === "") return null;
  return <div className={wide ? "md:col-span-2" : ""}><div className="text-[10.5px] uppercase tracking-widest text-[#A1A1AA] font-bold">{k}</div><div className="text-[13px] mt-0.5">{String(v)}</div></div>;
}
